/**
 * isiZulu: Flows and chatbots
 *
 * What this file covers: The chatbot flow builder, its steps, AI answers, Build with AI, and the chatbots
 * themselves.
 *
 * Tone: Practical and a little technical, never heavy with jargon. Step names are short actions (Ask a
 * question, Book a time). Explanations say what the step does for the client.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flows: Catalog = {
  'flows.title': 'Ukugeleza',
  'flows.builder.title': 'Umakhi',
  'flows.builder.saveFailed': 'Ukulondoloza kwehlulekile',
  'flows.builder.unsaved': 'Izinguquko ezingalondolozwanga',
  'flows.builder.canvasTab': 'Ikhanvasi',
  'flows.builder.palette': 'Amanodi',
  'flows.builder.properties': 'Izakhiwo',
  'flows.builder.group.start': 'Qala',
  'flows.builder.group.steps': 'Izinyathelo',
  'flows.builder.node.trigger': 'Isiqalisi',
  'flows.builder.node.reply': 'Thumela impendulo',
  'flows.builder.node.handoff': 'Dlulisela',
  'flows.builder.field.keyword': 'Igama elingukhiye',
  'flows.builder.field.body': 'Umlayezo',
  'flows.drawer.definition': 'Incazelo',
  'flows.drawer.nameRequired': 'Igama liyadingeka.',
  'flows.empty': 'Akukho ukugeleza okwamanje: dala okukodwa ukuze uzenzakalele izimpendulo.',
  'flows.loadError': 'Ayikwazanga ukulayisha ukugeleza.',
  'flows.modal.delete.confirm': 'Susa ukugeleza',
  'flows.modal.delete.title': 'Susa ukugeleza?',
  'flows.runs.empty': 'Azikho izinhlelo okwamanje kulokhu kugeleza.',
  'flows.view.backToAll': 'Konke ukugeleza',
  'flows.view.lifecycle.activateFlow': 'Yenza ukugeleza kusebenze',
  'flows.view.lifecycle.activation': 'Ukwenza kusebenze',
  'flows.view.lifecycle.deactivateFlow': 'Misa ukugeleza',
  'flows.view.lifecycle.deleteFailed': 'Ayikwazanga ukususa ukugeleza.',
  'flows.view.lifecycle.updateFailed': 'Ayikwazanga ukubuyekeza ukugeleza.',
  'flows.view.overview.flowId': 'I-ID yokugeleza',
  'flows.view.overview.title': 'Imininingwane yokugeleza',
  'flows.view.runs.columns.contact': 'Oxhumana naye',
  'flows.view.runs.columns.duration': 'Ubude besikhathi',
  'flows.view.runs.columns.error': 'Iphutha',
  'flows.view.runs.title': 'Izinhlelo zakamuva',
  'flows.view.tabs.runs': 'Izinhlelo',
  'flows.actions.create': 'Dala uhlelo',
  'flows.showingOf': 'Kukhonjiswa {{count}} kwangu-{{total}}',
  'flows.node.trigger': 'Isiqalisi',
  'flows.node.reply': 'Thumela impendulo',
  'flows.builder.save': 'Londoloza izinguquko',
  'flows.connect.metaChannelLive': 'iphendula nalapha, uma nje ikhasi lixhunyiwe.',
  'flows.connect.metaChannelWhere': 'Xhuma ikhasi le-Facebook noma i-akhawunti ye-Instagram ku-',
  'bots.switcher.label': 'I-chatbot',
  'bots.switcher.hint': 'Ubuka umsebenzi wale chatbot.',
  'bots.switcher.flowsHint': 'Ukugeleza okungezansi kungokwale chatbot. Ukugeleza okusha kwengezwa kuyo.',
  'bots.switcher.knowledgeHint': 'I-chatbot ngayinye inezimpendulo zayo ezizenzakalelayo. Imibhalo yabelwana ngayo yonke.',
  'bots.switcher.defaultOption': '{{name}} (ezenzakalelayo)',
  'bots.channels.title': 'Lapho le chatbot iphendula khona',
  'bots.channels.intro': 'Khetha izinombolo, izingxoxo zewebhusayithi nama-akhawunti le chatbot okufanele iphendule kuwo. Ngayinye iphendulwa yi-chatbot eyodwa, ngakho ukuyikhetha lapha kuyisusa kwi-chatbot ebinayo.',
  'bots.channels.introDefault': 'Lena yi-chatbot yakho ezenzakalelayo: iphendula yonke indawo lapho kungakhethwanga enye i-chatbot. Ukuze unike enye i-chatbot inombolo noma ingxoxo, vula leyo chatbot bese uyikhetha lapho.',
  'bots.channels.noFlows': '{{name}} ayikabi nakho ukugeleza, ngakho amakhasimende abhala lapha awatholi mpendulo. Yakha okukodwa ku:',
  'bots.channels.none': 'Awukaxhumi zinombolo, zingxoxo zewebhusayithi noma ama-akhawunti. Qala ku:',
  'bots.channels.kind.whatsapp': 'WhatsApp',
  'bots.channels.kind.web': 'Ingxoxo yewebhusayithi',
  'bots.channels.kind.messenger': 'Messenger',
  'bots.channels.kind.instagram': 'Instagram',
  'bots.channels.mineByDefault': 'Iphendula lapha ngoba ayikho enye i-chatbot ekhethiwe.',
  'bots.channels.answeredBy': 'Manje kuphendulwa ngu-{{name}}.',
  'bots.channels.moved': 'I-{{account}} manje iphendulwa ngu-{{name}}.',
  'bots.channels.returned': 'I-{{account}} ibuyele ku-{{name}}.',
  'bots.channels.confirm.title': 'Uyidlulisele kule chatbot?',
  'bots.channels.confirm.body': 'I-{{account}} iphendulwa ngu-{{from}} namuhla. Kusukela emyalezweni olandelayo izophendulwa ngu-{{to}}. Ingxoxo esivele iqhubeka izoqedwa ngu-{{from}}.',
  'bots.channels.confirm.action': 'Yidlulise',
  'bots.actions.title': 'Okuzenzakalelayo nokususa',
  'bots.actions.isDefault': 'Lena yi-chatbot yakho ezenzakalelayo. Iphendula kuzo zonke izinombolo, izingxoxo zewebhusayithi nama-akhawunti anganikwanga enye i-chatbot, ngakho ayikwazi ukususwa. Ukuze uyisuse, qala wenze enye i-chatbot ibe ngezenzakalelayo.',
  'bots.actions.defaultHint': 'I-chatbot ezenzakalelayo iphendula kuzo zonke izinombolo, izingxoxo zewebhusayithi nama-akhawunti anganikwanga enye i-chatbot.',
  'bots.actions.madeDefault': 'U-{{name}} manje uyi-chatbot yakho ezenzakalelayo.',
  'bots.actions.deleted': 'U-{{name}} ususiwe.',
  'bots.actions.makeDefault.title': 'Wenze u-{{name}} abe ngozenzakalelayo?',
  'bots.actions.makeDefault.body': 'U-{{name}} uzophendula yonke indawo lapho kungakhethwanga i-chatbot, kusukela emyalezweni olandelayo. Ukugeleza kuhlala ne-chatbot okungokwayo.',
  'bots.actions.makeDefault.action': 'Yenza ezenzakalelayo',
  'bots.actions.delete.title': 'Susa u-{{name}}?',
  'bots.actions.delete.body': 'Le chatbot ayinakho ukugeleza futhi ayiphenduli ndawo, ngakho akukho okunye okushintshayo. Izingxoxo zangaphambilini zigcina umlando wazo.',
  'bots.actions.delete.action': 'Susa i-chatbot',
  'bots.actions.delete.hint': 'Ukususa kukhipha igama nezilungiselelo zale chatbot. Izingxoxo zangaphambilini zigcina umlando wazo.',
  'bots.actions.delete.hintInUse': 'Le chatbot inokugeleza noma iphendula ku-akhawunti. Uzobuzwa ukuthi iyiphi i-chatbot ezokuthatha.',
  'bots.actions.move.body': 'U-{{name}} usenomsebenzi: ukugeleza {{flows}}, ama-akhawunti {{accounts}}. Khetha i-chatbot ezokuthatha. Kudluliswa ngesinyathelo esisodwa nokususa.',
  'bots.actions.move.label': 'Dlulisela konke ku',
  'bots.actions.move.hint': 'Leyo chatbot izosebenzisa lokhu kugeleza futhi iphendule kula ma-akhawunti kusukela emyalezweni olandelayo.',
  'bots.actions.move.action': 'Dlulisa bese ususa',
  'bots.add.title': 'Engeza i-chatbot',
  'bots.add.intro': 'I-chatbot inegama layo, indlela yayo yokukhuluma nokugeleza kwayo. Ngemva kokuyengeza, khetha lapho iphendula khona bese wakha ukugeleza kwayo.',
  'bots.add.copyFrom': 'Qala kusuka ku',
  'bots.add.copyFromHint': 'Kukopisha ithoni, ulimi, izimpendulo zengxoxo encane, uhlu lwamagama nezaziso. Ukugeleza nama-akhawunti akukopishwa.',
  'bots.add.copyNothing': 'I-chatbot engenalutho',
  'bots.add.action': 'Engeza i-chatbot',
  'bots.add.done': 'U-{{name}} wengeziwe. Khetha lapho ephendula khona, bese wakha ukugeleza kwakhe.',
  'bots.add.cardTitle': 'Enye i-chatbot',
  'bots.add.cardBody': 'Phatha uhlobo lwesibili, ulimi noma umnyango ngegama lawo, ukugeleza nezinombolo zawo. I-chatbot eyodwa ingaphendula ezinombolweni eziningi, noma inombolo ngayinye ibe neyayo.',
  'bots.add.atLimit': 'Ama-chatbot afakiwe ku-{{plan}}: {{count}}. Thuthukela ohlelweni olukhulu ukuze wengeze enye.',
  'bots.add.seePlans': 'Bona izinhlelo',
  'bots.list.title': 'Ama-chatbot akho',
  'bots.list.intro': 'I-chatbot ngayinye inegama layo, ukugeleza kwayo nezinombolo nezingxoxo ephendula kuzo. Vula eyodwa ukuze uyishintshe.',
  'bots.list.default': 'Ezenzakalelayo',
  'bots.list.answers': 'Izinombolo nezingxoxo ezixhunyiwe: {{count}}',
  'bots.list.answersDefault': 'Iphendula yonke indawo lapho kungakhethwanga enye i-chatbot',
  'bots.list.flows': 'Ukugeleza: {{count}}',
  'bots.loadError': 'Ama-chatbot akho awakwazanga ukulayishwa. Vuselela ikhasi ukuze uzame futhi.',
  'bots.backToList': 'Buyela kuma-chatbot akho',
  'bots.notFound': 'Leyo chatbot ayisekho.',
  'bots.flow.label': 'I-chatbot',
  'bots.flow.hint': 'Yile chatbot kuphela esebenzisa lokhu kugeleza. Ukukudlulisa kuqala emyalezweni olandelayo.',
  'bots.header.count': '{{current}}/{{allowance}} · {{plan}}',
  'bots.header.countUnlimited': '{{current}} · {{plan}}, akunamkhawulo',
  'bots.header.single': 'U-{{name}} uphendula kuzo zonke izinombolo zakho nezingxoxo. Engeza i-chatbot ukuze uphathe uhlobo lwesibili, ulimi noma umnyango ngegama lawo, ukugeleza nezinombolo zawo.'
};

export default flows;
